import * as api from "../api/idderocloud-api";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getDevice, getUserDevices } from "./devices-slice";
import { logout } from "./auth-slice";
import { oemValues } from "../constants/oem";

let fakeVoiceDeviceData = null;
if (
  process.env.NODE_ENV !== "production" &&
  process.env.REACT_APP_TEST_LOCALIZATION === "true"
) {
  fakeVoiceDeviceData = require("../data/test/fakeVoiceDeviceData.json");
}

export const assignLicense = createAsyncThunk(
  "voice/assignLicense",
  async (data, { rejectWithValue }) => {
    try {
      await api.assignLicenseToDevice(data);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const checkDeviceStatus = createAsyncThunk(
  "voice/checkDeviceStatus",
  async (cloudId, { rejectWithValue }) => {
    try {
      const status = await api.checkDeviceStatus(cloudId);
      return status;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const assignPinToDevice = createAsyncThunk(
  "voice/assignPinToDevice",
  async (data, { rejectWithValue }) => {
    try {
      await api.assignPinToDevice(data);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const refreshDeviceStatus = createAsyncThunk(
  "voice/refreshDeviceStatus",
  async (cloudId, { rejectWithValue }) => {
    if (process.env.NODE_ENV !== "production" && cloudId === "testdev")
      return { status: "ok" };

    try {
      const status = await api.refreshDevice(cloudId);
      return status;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
  {
    condition: (cloudId, { getState }) => {
      // do not emit more requests if previous is still in progress
      // https://redux-toolkit.js.org/api/createAsyncThunk#canceling-before-execution
      const state = getState();
      if (state.voiceControl.devices[cloudId].status === "checking")
        return false;
    },
  }
);

export const getControls = createAsyncThunk(
  "voiceControl/getControls",
  async (cloudId, { rejectWithValue }) => {
    if (process.env.NODE_ENV !== "production" && cloudId === "testdev") {
      return {
        cloudId,
        controls: fakeVoiceDeviceData.controls,
        zones: fakeVoiceDeviceData.zones,
      };
    }

    try {
      const controls = await api.getControls(cloudId);
      return { cloudId, controls: controls.controls, zones: controls.zones };
    } catch (err) {
      return rejectWithValue(err);
    }
  },
  {
    condition: (cloudId, { getState }) => {
      const state = getState();
      if (state.voiceControl.devices[cloudId].loading) return false;
    },
  }
);

export const addControl = createAsyncThunk(
  "voiceControl/addControl",
  async (deviceControl, { rejectWithValue }) => {
    try {
      const { cloudId, control } = deviceControl;
      const newControl = {
        ...JSON.parse(deviceControl.control.component),
        friendly_name: control.name,
        type: control.type,
      };
      await api.saveControl({ cloudId: cloudId, control: newControl });
      return { cloudId, newControl };
    } catch (err) {
      return rejectWithValue(err);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      if (state.voiceControl.addInProgress) return false;
    },
  }
);

export const deleteControl = createAsyncThunk(
  "voiceControl/deleteControl",
  async (deviceControl, { rejectWithValue }) => {
    try {
      await api.deleteControl(deviceControl);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      if (state.voiceControl.deleteInProgress) return false;
    },
  }
);

export const deleteControls = createAsyncThunk(
  "voiceControl/deleteControls",
  async (cloudId, { rejectWithValue }) => {
    try {
      await api.deleteControls(cloudId);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      if (state.voiceControl.deleteInProgress) return false;
    },
  }
);

function unselectedDevice() {
  return { status: "unselected", configured: [], not_configured: [] };
}

function getInitialState() {
  return {
    selected: unselectedDevice(),
    ...getEditInitialState(),
    ...getLicenseFormInitialState(),
    ...getPinFormInitialState(),
    devices: {},
  };
}

function getVoiceCompatibleDevices(devices) {
  return devices
    ?.filter((device) => oemValues.voiceCompatible.includes(device.model))
    .reduce(
      (deviceList, device) => ({
        ...deviceList,
        [device.cloudId]: {
          status: device.voice_control ? "" : "available",
          loading: false,
          loaded: false,
          error: null,
          configured: [],
          not_configured: [],
          zones: [],
        },
      }),
      {}
    );
}

function getEditInitialState() {
  return {
    error: false,
    addInProgress: false,
    deleteInProgress: false,
  };
}

function getLicenseFormInitialState() {
  return {
    error: false,
    checkDeviceInProgress: false,
    assignLicenseInProgress: false,
    isFirmwareVoiceCompatible: false,
  };
}

function getPinFormInitialState() {
  return {
    assignPinInProgress: false,
    pinError: null,
    pinDialogStatus: null,
  };
}

const initialState = getInitialState();

export const voiceControlSlice = createSlice({
  name: "voiceControl",
  initialState,
  reducers: {
    selectDevice: (state, action) => {
      state.selected = action.payload ? action.payload : unselectedDevice();
    },
    initEdit: (state) => {
      Object.assign(state, getEditInitialState());
    },
    initLicenseForm: (state) => {
      Object.assign(state, getLicenseFormInitialState());
    },
    initPinForm: (state) => {
      Object.assign(state, getPinFormInitialState());
    },
    testSetTestingDeviceStatus: (state, action) => {
      state.devices.testdev.status = action.payload;
      if (action.payload === "available") {
        state.isFirmwareVoiceCompatible = false;
      }
    },
    testSetTestingDeviceError: (state, action) => {
      state.devices.testdev.status = "generic_error";
      state.devices.testdev.error = action.payload;
    },
    testSetGlobalError: (state, action) => {
      state.error = action.payload;
    },
    testSetIsFirmwareVoiceCompatibleTrue: (state) => {
      state.isFirmwareVoiceCompatible = true;
    },
    testEmptyTestingDeviceControls: (state) => {
      state.devices.testdev.configured = [];
      state.devices.testdev.not_configured = [];
      state.devices.testdev.zones = [];
    },
    testUpdatePinDialog: (state, action) => {
      state.pinDialogStatus = action.payload.status;
      if (action.payload.status === "success") {
        state.pinError = null;
      }
      if (action.payload.error) {
        state.pinError = { type: action.payload.error };
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(checkDeviceStatus.fulfilled, (state, action) => {
      state.checkDeviceInProgress = false;
      state.error = false;
      if (action.payload.status === "ok") {
        state.isFirmwareVoiceCompatible = true;
      }
    });
    builder.addCase(checkDeviceStatus.rejected, (state, action) => {
      if (action.error.name === "AbortError") {
        Object.assign(state, getLicenseFormInitialState());
      } else {
        state.checkDeviceInProgress = false;
        state.error = action.payload;
      }
    });
    builder.addCase(checkDeviceStatus.pending, (state) => {
      state.checkDeviceInProgress = true;
    });
    builder.addCase(assignLicense.fulfilled, (state, action) => {
      state.assignLicenseInProgress = false;
      state.error = false;
      if (state.devices[action.meta.arg.cloudId]) {
        state.devices[action.meta.arg.cloudId].status = "licenseAssigned";
      }
    });
    builder.addCase(assignLicense.rejected, (state, action) => {
      if (action.error.name === "AbortError") {
        Object.assign(state, getLicenseFormInitialState());
      } else {
        state.assignLicenseInProgress = false;
        state.error = action.payload;
      }
    });
    builder.addCase(assignLicense.pending, (state) => {
      state.assignLicenseInProgress = true;
    });
    builder.addCase(assignPinToDevice.fulfilled, (state) => {
      state.assignPinInProgress = false;
      state.pinDialogStatus = "success";
    });
    builder.addCase(assignPinToDevice.rejected, (state, action) => {
      state.assignPinInProgress = false;
      state.pinError = action.payload;
      state.pinDialogStatus = "error";
    });
    builder.addCase(assignPinToDevice.pending, (state) => {
      state.assignPinInProgress = true;
      state.pinErrorMessage = null;
      state.pinDialogStatus = null;
    });
    builder.addCase(getControls.pending, (state, action) => {
      state.devices[action.meta.arg].loading = true;
    });
    builder.addCase(getControls.fulfilled, (state, action) => {
      const { cloudId, controls, zones } = action.payload;
      state.devices[cloudId] = {
        ...state.devices[cloudId],
        configured: controls.filter((component) =>
          component.hasOwnProperty("type")
        ),
        not_configured: controls.filter(
          (component) => !component.hasOwnProperty("type")
        ),
        zones,
        loading: false,
        loaded: true,
      };
      if (!zones.length) {
        state.devices[cloudId].status = "empty";
      }
    });
    builder.addCase(getControls.rejected, (state, action) => {
      state.devices[action.meta.arg].loading = false;
      state.devices[action.meta.arg].loaded = false;
      state.devices[action.meta.arg].status = "generic_error";
      if (action.payload.type) {
        state.devices[action.meta.arg].error = action.payload.type;
      }
    });
    builder.addCase(addControl.pending, (state) => {
      state.addInProgress = true;
    });
    builder.addCase(addControl.fulfilled, (state, action) => {
      const { cloudId, newControl } = action.payload;
      const controlIndex = state.devices[cloudId].configured.findIndex(
        (controls) => controls.id === newControl.id
      );
      controlIndex !== -1
        ? (state.devices[cloudId].configured[controlIndex] = newControl)
        : (state.devices[cloudId].configured = [
            ...state.devices[cloudId].configured,
            newControl,
          ]);
      state.devices[cloudId].not_configured = state.devices[
        cloudId
      ].not_configured.filter((controls) => controls.id !== newControl.id);
      state.addInProgress = false;
    });
    builder.addCase(addControl.rejected, (state, action) => {
      state.error = true;
      state.addInProgress = false;
      if (action.payload.type) {
        state.devices[action.meta.arg.cloudId].error = action.payload.type;
      }
    });
    builder.addCase(refreshDeviceStatus.pending, (state, action) => {
      state.devices[action.meta.arg].status = "checking";
      state.devices[action.meta.arg].loaded = false;
    });
    builder.addCase(refreshDeviceStatus.fulfilled, (state, action) => {
      state.devices[action.meta.arg].status = action.payload.status;
    });
    builder.addCase(refreshDeviceStatus.rejected, (state, action) => {
      state.devices[action.meta.arg].status = "generic_error";
      if (action.payload.type) {
        state.devices[action.meta.arg].error = action.payload.type;
      }
    });
    builder.addCase(deleteControl.pending, (state) => {
      state.deleteInProgress = true;
    });
    builder.addCase(deleteControl.fulfilled, (state, action) => {
      state.error = false;
      state.deleteInProgress = false;
      const component = state.devices[action.meta.arg.cloudId].configured.find(
        (control) => control.id === action.meta.arg.controlId
      );
      const configured = state.devices[action.meta.arg.cloudId].configured;
      state.devices[action.meta.arg.cloudId].configured = configured.filter(
        (control) => control.id !== action.meta.arg.controlId
      );
      state.devices[action.meta.arg.cloudId].not_configured = [
        ...state.devices[action.meta.arg.cloudId].not_configured,
        component,
      ];
    });
    builder.addCase(deleteControl.rejected, (state, action) => {
      state.error = true;
      state.deleteInProgress = false;
      if (action.payload.type) {
        state.devices[action.meta.arg.cloudId].error = action.payload.type;
      }
    });
    builder.addCase(deleteControls.pending, (state) => {
      state.deleteInProgress = true;
    });
    builder.addCase(deleteControls.fulfilled, (state, action) => {
      state.deleteInProgress = false;
      state.devices[action.meta.arg].status = "";
    });
    builder.addCase(deleteControls.rejected, (state, action) => {
      state.deleteInProgress = false;
      state.devices[action.meta.arg].status = "generic_error";
      if (action.payload.type) {
        state.devices[action.meta.arg].error = action.payload.type;
      }
    });
    builder.addCase(logout.fulfilled, () => {
      return { ...getInitialState() };
    });
    builder.addCase(getUserDevices.fulfilled, (state, action) => {
      state.devices = {
        ...getVoiceCompatibleDevices(action.payload),
        ...state.devices,
      };
    });
    builder.addCase(getDevice.fulfilled, (state, action) => {
      state.devices = {
        ...getVoiceCompatibleDevices([action.payload]),
        ...state.devices,
      };
    });
  },
});

export const {
  selectDevice,
  initEdit,
  initLicenseForm,
  initPinForm,
  testSetTestingDeviceStatus,
  testSetTestingDeviceError,
  testSetGlobalError,
  testSetIsFirmwareVoiceCompatibleTrue,
  testEmptyTestingDeviceControls,
  testUpdatePinDialog,
} = voiceControlSlice.actions;

export const getError = (state) => state.voiceControl.error;
export const getSelectedDeviceData = (state) => {
  return {
    ...state.voiceControl.selected,
    ...state.voiceControl.devices[state.voiceControl.selected.cloudId],
  };
};
export const getDeviceVoiceData = (cloudId) => (state) =>
  state.voiceControl.devices[cloudId];

export const getInProgressState = ({ voiceControl }) => {
  return {
    addInProgress: voiceControl.addInProgress,
    deleteInProgress: voiceControl.deleteInProgress,
  };
};

export const getLicenseFormState = ({ voiceControl }) => {
  return {
    checkDeviceInProgress: voiceControl.checkDeviceInProgress,
    assignLicenseInProgress: voiceControl.assignLicenseInProgress,
    isFirmwareVoiceCompatible: voiceControl.isFirmwareVoiceCompatible,
  };
};

export const getPinFormState = ({ voiceControl }) => {
  return {
    assignPinInProgress: voiceControl.assignPinInProgress,
    pinError: voiceControl.pinError,
    pinDialogStatus: voiceControl.pinDialogStatus,
  };
};

export const voiceControlReducer = voiceControlSlice.reducer;
